import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '../lib/api.js';
import { UploadPreview } from './fhir/UploadPreview.js';
import { FhirSpecBuilder } from './fhir/FhirSpecBuilder.js';
import { FhirCacheView } from './fhir/FhirCacheView.js';
import { CerboDraftView } from './fhir/CerboDraftView.js';
import { ImportConfigForm } from './fhir/ImportConfigForm.js';
import { DocumentDetectPanel } from './fhir/DocumentDetectPanel.js';
import { ImportResults } from './fhir/ImportResults.js';
import { RecipeReuseBar } from './RecipeReuseBar.js';
import { LogStream, type LogLine } from './LogStream.js';
import { deriveImportSteps } from '../lib/session-ui.js';

type Upload = { id: string; filename: string; headers?: string[] | null };
type Row = { uploadId: string; specId: string };

const STAGE_LABEL: Record<string, string> = { extract: 'Extracting', map: 'Mapping', cache: 'Caching', load: 'Loading', export: 'Importing' };
const stageLabel = (s?: string): string => (s ? (STAGE_LABEL[s] ?? 'Working') : 'Importing');

/**
 * FHIR import launcher for a flat-file session — the dashboard face of the
 * @ai/fhir migration path (profiled-subset resources → Cerbo via the
 * fhir-import queue). The operator builds a list of {file → accepted spec}
 * pairings and launches them in ONE start-fhir-import call; the worker loads
 * Patient first and resolves cross-file references via the per-run ledger, so a
 * multi-file drop (patients + conditions + allergies …) lands linked. Includes
 * a JSON-editor authoring stopgap until an AI FHIR scoper exists.
 */
export function FhirImportPanel({
  sessionId,
  uploads,
  activeUploadId,
  reveal = false,
}: {
  sessionId: string;
  uploads: Upload[];
  activeUploadId: string | null;
  reveal?: boolean;
}) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['fhir-specs', sessionId],
    queryFn: () => api.listFhirMappingSpecs(sessionId),
    refetchInterval: 4000,
  });
  const accepted = (data?.specs ?? []).filter((s) => s.status === 'accepted');

  // Authoritative session status drives the pause/resume/abort controls.
  const { data: sessionData } = useQuery({
    queryKey: ['session-status', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 2000,
  });
  const sessionStatus = sessionData?.session.status;
  const ehrId = sessionData?.session.ehrId ?? null;

  // Live import status from the session SSE stream. The worker publishes stage.*
  // (export) events; the stream replays persisted events on connect, so a
  // finished import still shows "done". Named SSE events → addEventListener
  // (not onmessage), mirroring the EHR LiveLog.
  const [importStatus, setImportStatus] = useState<{ state: 'idle' | 'active' | 'done' | 'error'; count: number; stage?: string }>({
    state: 'idle',
    count: 0,
  });
  // Live worker log lines (SSE `log` events) — the flat-file console (gaps-plan G2).
  const [logs, setLogs] = useState<LogLine[]>([]);
  useEffect(() => {
    const es = new EventSource(`/api/stream?topics=${encodeURIComponent(`session:${sessionId}`)}`);
    const FLOW = new Set(['extract', 'map', 'cache', 'load', 'export']);
    const onEvt = (base: 'active' | 'finished') => (ev: MessageEvent) => {
      try {
        const { payload } = JSON.parse(ev.data) as { payload: { stage?: string; status?: string; completed?: number } };
        const stage = payload.stage;
        if (!stage || !FLOW.has(stage)) return;
        setImportStatus((prev) => {
          if (base === 'finished') {
            // The 'export' envelope is authoritative for the terminal state;
            // sub-stage (map/load) finishes keep the run 'active'.
            if (stage === 'export') return { state: payload.status === 'error' ? 'error' : 'done', count: prev.count, stage };
            return prev;
          }
          return { state: 'active', count: payload.completed ?? prev.count, stage };
        });
      } catch {
        /* ignore malformed */
      }
    };
    es.addEventListener('stage.started', onEvt('active'));
    es.addEventListener('stage.progress', onEvt('active'));
    es.addEventListener('stage.finished', onEvt('finished'));
    es.addEventListener('log', (ev: MessageEvent) => {
      try {
        const evt = JSON.parse(ev.data) as { seq: number; ts: string; payload: { severity: LogLine['severity']; message: string; component?: string; meta?: Record<string, unknown> } };
        setLogs((prev) => [...prev.slice(-2000), { id: evt.seq, ts: new Date(evt.ts).toISOString().slice(11, 23), severity: evt.payload.severity, message: evt.payload.message, component: evt.payload.component, meta: evt.payload.meta }]);
      } catch {
        /* ignore malformed */
      }
    });
    return () => es.close();
  }, [sessionId]);

  const [rows, setRows] = useState<Row[]>([{ uploadId: '', specId: '' }]);
  // Seed the first row's empty fields as uploads + specs arrive. Seeds each
  // field independently (uploads and specs load on different ticks) and only
  // while there's a single untouched row, so it never clobbers an operator edit.
  useEffect(() => {
    setRows((prev) => {
      if (prev.length !== 1) return prev;
      const r = prev[0];
      if (!r || (r.uploadId && r.specId)) return prev;
      const seeded = {
        uploadId: r.uploadId || activeUploadId || uploads[0]?.id || '',
        specId: r.specId || accepted[0]?.id || '',
      };
      return seeded.uploadId === r.uploadId && seeded.specId === r.specId ? prev : [seeded];
    });
  }, [uploads, accepted, activeUploadId]);

  const setRow = (i: number, patch: Partial<Row>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { uploadId: '', specId: '' }]);
  const removeRow = (i: number) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));
  const validRows = rows.filter((r) => r.uploadId && r.specId);

  const [showAdd, setShowAdd] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showCache, setShowCache] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  // Destination for this launch: Cerbo (default), or a flat-file export off the
  // same FHIR cache (NDJSON / CSV) — the hub's "→ anything" seam.
  const [dest, setDest] = useState<'cerbo' | 'fhir-ndjson' | 'fhir-csv'>('cerbo');
  // The upload the preview + mapper target — the operator's active file.
  const activeId = activeUploadId ?? uploads[0]?.id ?? null;
  const activeHeaders = uploads.find((u) => u.id === activeId)?.headers ?? [];

  // Infer EVERY resource the active file supports (Patient + a clinical spec per
  // signal), accept them, and pair the file with each — one wide file → all its
  // resources in one launch.
  const autoMapAll = useMutation({
    mutationFn: async (): Promise<string[]> => {
      if (!activeId || activeHeaders.length === 0) throw new Error('The active file has no detected headers to infer from.');
      const { specs } = await api.inferFhirMappingSpecs(sessionId, activeHeaders);
      const ids: string[] = [];
      for (const spec of specs) {
        const { specId } = await api.createFhirMappingSpec(sessionId, spec, { status: 'accepted', authoredBy: 'human' });
        ids.push(specId);
      }
      return ids;
    },
    onSuccess: (ids) => {
      qc.invalidateQueries({ queryKey: ['fhir-specs', sessionId] });
      setRows((rs) => [...rs.filter((r) => r.uploadId && r.specId), ...ids.map((specId) => ({ uploadId: activeId!, specId }))]);
    },
  });
  // The (upload, spec) pairing the Cerbo-draft preview maps: the first valid row,
  // else the active upload + the first accepted spec.
  const draftPair = validRows[0] ?? (activeId && accepted[0] ? { uploadId: activeId, specId: accepted[0].id } : null);
  const draftSpec = accepted.find((s) => s.id === draftPair?.specId)?.spec as Record<string, unknown> | undefined;

  // WS2.1 — guided workflow: derive each step's status from real session state so
  // the operator sees where they are + what's left, and can't launch an incomplete
  // mapping. A row is "partial" if it has a file XOR a spec (not both).
  const partialRow = rows.some((r) => (r.uploadId ? 1 : 0) + (r.specId ? 1 : 0) === 1);
  const launchReady = validRows.length > 0 && !partialRow;
  const isRunning = sessionStatus === 'running' || importStatus.state === 'active';
  const isDone = importStatus.state === 'done' || (sessionStatus === 'completed' && !isRunning);
  const launchHint = partialRow
    ? 'Finish every row — each needs both a file and an accepted spec'
    : validRows.length === 0
      ? 'Pair at least one file with an accepted spec to launch'
      : 'Launch the FHIR import';
  const steps = deriveImportSteps({
    uploadCount: uploads.length,
    acceptedSpecCount: accepted.length,
    launchReady,
    isRunning,
    isDone,
  });

  const launch = useMutation({
    mutationFn: () => {
      if (validRows.length === 0) throw new Error('Pair at least one file with a spec');
      const first = accepted.find((s) => s.id === validRows[0]?.specId);
      const sourceSystem = (first?.spec.sourceSystem as string | undefined) ?? first?.sourceSystem ?? 'flat-file';
      return api.startFhirImport(sessionId, {
        sourceSystem,
        files: validRows.map((r) => ({ uploadId: r.uploadId, specId: r.specId })),
        destinationKind: dest,
      });
    },
    onSuccess: () => {
      const where = dest === 'cerbo' ? 'Cerbo' : dest === 'fhir-csv' ? 'CSV files' : 'FHIR NDJSON';
      toast.success(`Import launched → ${where} (${validRows.length} file${validRows.length > 1 ? 's' : ''})`);
      void qc.invalidateQueries({ queryKey: ['stats', sessionId] });
      void qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
    },
    onError: (e) => toast.error(`Launch failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Abort a running import (sets the session control flag; the worker bails
  // between files / every 100 rows). Already-loaded rows stay — a re-launch
  // resumes idempotently.
  const abort = useMutation({
    mutationFn: () => api.abortSession(sessionId),
    onSuccess: () => {
      toast.success('Import aborting…');
      void qc.invalidateQueries({ queryKey: ['stats', sessionId] });
      void qc.invalidateQueries({ queryKey: ['sessions', sessionId] });
    },
    onError: (e) => toast.error(`Abort failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  // Pause/resume via the session control key (the worker blocks at the next
  // checkpoint while 'paused', resumes when cleared).
  const pause = useMutation({
    mutationFn: () => api.pauseSession(sessionId),
    onSuccess: () => {
      toast.success('Import paused');
      void qc.invalidateQueries({ queryKey: ['session-status', sessionId] });
    },
    onError: (e) => toast.error(`Pause failed: ${e instanceof Error ? e.message : String(e)}`),
  });
  const resume = useMutation({
    mutationFn: () => api.resumeSession(sessionId),
    onSuccess: () => {
      toast.success('Import resumed');
      void qc.invalidateQueries({ queryKey: ['session-status', sessionId] });
    },
    onError: (e) => toast.error(`Resume failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const selectStyle = { background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' } as const;

  return (
    <div className="glass rounded-2xl p-5" style={{ boxShadow: '0 3px 20px rgba(0,0,0,0.3)' }}>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-widest text-text-secondary">FHIR import</div>
        {importStatus.state === 'idle' ? (
          <div className="text-[11px] text-text-dimmed">profiled-subset → Cerbo (preview)</div>
        ) : (
          <div
            className="text-[11px] font-semibold"
            style={{ color: importStatus.state === 'error' ? '#f87171' : importStatus.state === 'done' ? '#34d399' : 'var(--accent-blue)' }}
          >
            {importStatus.state === 'active' && `● ${stageLabel(importStatus.stage)}…${importStatus.count ? ` ${importStatus.count}` : ''}`}
            {importStatus.state === 'done' && `● Imported ${importStatus.count}`}
            {importStatus.state === 'error' && '● Import failed'}
          </div>
        )}
      </div>

      {/* WS2.1 — guided step strip: Preview → Map → Pair → Launch → Monitor → Results */}
      <div className="mb-3 flex flex-wrap items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider">
        {steps.map((s, i) => (
          <div key={s.label} className="flex items-center gap-1.5">
            {i > 0 && <span style={{ color: 'var(--text-dimmed)' }}>›</span>}
            <span
              className="flex items-center gap-1 rounded-full px-2 py-0.5"
              style={{
                background: s.state === 'current' ? 'rgba(139,92,246,0.18)' : 'var(--bg-primary)',
                border: `1px solid ${s.state === 'current' ? 'var(--accent-blue)' : 'var(--border)'}`,
                color: s.state === 'done' ? '#34d399' : s.state === 'current' ? 'var(--accent-blue)' : 'var(--text-dimmed)',
              }}
            >
              <span>{s.state === 'done' ? '✓' : i + 1}</span>
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* Recipe reuse / authoring — deterministic-first (design §5) */}
      <RecipeReuseBar
        sessionId={sessionId}
        ehrId={ehrId}
        activeUploadId={activeId}
        activeHeaders={activeHeaders}
        acceptedSpecs={accepted.map((s) => ({ id: s.id, spec: s.spec, resourceType: s.resourceType, sourceSystem: s.sourceSystem }))}
        onApplyRecipe={(specIds) => {
          if (!activeId) return;
          setRows((rs) => [...rs.filter((r) => r.uploadId && r.specId), ...specIds.map((specId) => ({ uploadId: activeId, specId }))]);
        }}
      />

      {accepted.length === 0 ? (
        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          No accepted FHIR specs yet — add one to enable launch.
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Files → specs
          </div>
          {rows.map((row, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                value={row.uploadId}
                onChange={(e) => setRow(i, { uploadId: e.target.value })}
                className="flex-1 rounded-lg px-3 py-1.5 text-sm outline-none"
                style={selectStyle}
              >
                <option value="">— file —</option>
                {uploads.map((u) => (
                  <option key={u.id} value={u.id}>{u.filename}</option>
                ))}
              </select>
              <span style={{ color: 'var(--text-dimmed)' }}>→</span>
              <select
                value={row.specId}
                onChange={(e) => setRow(i, { specId: e.target.value })}
                className="flex-1 rounded-lg px-3 py-1.5 text-sm outline-none"
                style={selectStyle}
              >
                <option value="">— spec —</option>
                {accepted.map((s) => (
                  <option key={s.id} value={s.id}>{s.resourceType} — {s.sourceSystem}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={rows.length <= 1}
                title="Remove"
                className="rounded-lg px-2 py-1.5 text-sm disabled:opacity-30"
                style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="text-[11px] font-semibold"
            style={{ color: 'var(--accent-blue)' }}
          >
            + Add file
          </button>
          <button
            type="button"
            onClick={() => autoMapAll.mutate()}
            disabled={autoMapAll.isPending || !activeId || activeHeaders.length === 0}
            className="ml-3 text-[11px] font-semibold"
            style={{ color: 'var(--accent-purple)', opacity: autoMapAll.isPending || !activeId || activeHeaders.length === 0 ? 0.5 : 1 }}
            title="Infer + accept every resource type the active file supports, and pair the file with each"
          >
            {autoMapAll.isPending ? 'Auto-mapping…' : '✨ Auto-map all resources'}
          </button>
          {autoMapAll.error ? (
            <span className="ml-2 text-[11px]" style={{ color: 'var(--accent-red)' }}>{String(autoMapAll.error)}</span>
          ) : null}
        </div>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => launch.mutate()}
          disabled={!launchReady || launch.isPending}
          title={launchHint}
          className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }}
        >
          {launch.isPending ? 'Launching…' : `▶ Launch FHIR import${validRows.length > 1 ? ` (${validRows.length} files)` : ''}`}
        </button>
        <label className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-dimmed)' }}>
          to
          <select
            value={dest}
            onChange={(e) => setDest(e.target.value as typeof dest)}
            className="rounded-lg px-2 py-1.5 text-sm outline-none"
            style={selectStyle}
            title="Destination — the same FHIR cache exported to Cerbo, NDJSON, or CSV files"
          >
            <option value="cerbo">Cerbo</option>
            <option value="fhir-ndjson">FHIR NDJSON</option>
            <option value="fhir-csv">CSV files</option>
          </select>
        </label>
        {sessionStatus === 'running' && (
          <button
            type="button"
            onClick={() => pause.mutate()}
            disabled={pause.isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
          >
            {pause.isPending ? 'Pausing…' : '❚❚ Pause'}
          </button>
        )}
        {sessionStatus === 'paused' && (
          <button
            type="button"
            onClick={() => resume.mutate()}
            disabled={resume.isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
          >
            {resume.isPending ? 'Resuming…' : '▶ Resume'}
          </button>
        )}
        {(sessionStatus === 'running' || sessionStatus === 'paused') && (
          <button
            type="button"
            onClick={() => abort.mutate()}
            disabled={abort.isPending}
            className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)' }}
          >
            {abort.isPending ? 'Aborting…' : '■ Abort'}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowAdd((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showAdd ? 'Close mapper' : '+ Map spec'}
        </button>
        <button
          type="button"
          onClick={() => setShowPreview((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showPreview ? 'Hide raw data' : 'Raw Data preview'}
        </button>
        <button
          type="button"
          onClick={() => setShowCache((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showCache ? 'Hide cache' : 'Cache preview'}
        </button>
        <button
          type="button"
          onClick={() => setShowDraft((v) => !v)}
          disabled={!draftSpec}
          className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-40"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
          title={draftSpec ? 'Preview the real Cerbo rows this import would write' : 'Accept a mapping spec first'}
        >
          {showDraft ? 'Hide Cerbo draft' : 'Cerbo draft'}
        </button>
        <button
          type="button"
          onClick={() => setShowConfig((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showConfig ? 'Hide config' : 'Config'}
        </button>
        <button
          type="button"
          onClick={() => setShowDocs((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showDocs ? 'Hide documents' : 'Documents'}
        </button>
        <button
          type="button"
          onClick={() => setShowLogs((v) => !v)}
          className="rounded-lg px-3 py-2 text-sm font-medium"
          style={{ background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
        >
          {showLogs ? 'Hide logs' : `Logs${logs.length ? ` (${logs.length})` : ''}`}
        </button>
      </div>

      {(isDone || isRunning) && <ImportResults sessionId={sessionId} live={isRunning && !isDone} />}

      {showLogs && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Import log — live worker output
          </div>
          <div className="h-80">
            {logs.length ? (
              <LogStream lines={logs} />
            ) : (
              <div className="text-xs p-3 rounded-md" style={{ color: 'var(--text-dimmed)', background: 'var(--bg-primary)', border: '1px solid var(--border)' }}>
                No log lines yet — launch an import to stream the worker’s per-stage output here.
              </div>
            )}
          </div>
        </div>
      )}

      {showPreview && activeId && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Raw Data preview
          </div>
          <UploadPreview sessionId={sessionId} uploadId={activeId} />
        </div>
      )}
      {showAdd && activeId && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Map columns → FHIR
          </div>
          <FhirSpecBuilder
            sessionId={sessionId}
            uploadId={activeId}
            headers={activeHeaders}
            sourceSystem={(accepted[0]?.spec?.sourceSystem as string | undefined) ?? undefined}
            onSaved={() => {
              setShowAdd(false);
              void qc.invalidateQueries({ queryKey: ['fhir-specs', sessionId] });
            }}
          />
        </div>
      )}
      {showCache && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Common-schema cache
          </div>
          <FhirCacheView sessionId={sessionId} />
        </div>
      )}
      {showDraft && draftPair?.uploadId && draftSpec && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Cerbo draft (destination format — dry-run, no write)
          </div>
          <CerboDraftView sessionId={sessionId} uploadId={draftPair.uploadId} spec={draftSpec} reveal={reveal} />
        </div>
      )}
      {showConfig && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Import config
          </div>
          <ImportConfigForm sessionId={sessionId} />
        </div>
      )}
      {showDocs && (
        <div className="mt-3">
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>
            Documents — detect &amp; link to patients
          </div>
          <DocumentDetectPanel sessionId={sessionId} />
        </div>
      )}
    </div>
  );
}
